import React, {Fragment} from 'react';

import {CallMade, Add, CheckSharp} from '@material-ui/icons';
import {useHistory} from 'react-router-dom';
import {AppBar, Box, Divider, Grid} from '@material-ui/core';

import {EfeAccordion, EfeButtonPrimary, EfeButtonSecondary} from 'components';
import {
    EfeResponse,
    EfeViewsArray,
    LegalRepresentativeServiceRequest,
    LegalRepresentativesResponse,
    RealOwnerAdditionalInformation,
    RealOwnersResponse,
} from 'commons/models';
import {getRandomHex, nextStep, useViewSearch} from 'commons/utils/Functions';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {AppBarStyles} from 'assets/tss/commons';
import {MainContent} from 'layouts/commons';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {HistoryRoutes, Messages} from 'commons/constants';
import {RealOwnerInfo} from 'commons/types/Types';
import {ClientService} from 'services';
import {useAlert, useSpinner} from 'hooks';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';

const errorDetail = {
    data: {
        process: 'Propietario Real',
        message: ' ',
        errors: [Messages.LEGAL_REPRESENTATIVE_TRADED],
    },
};

const validateTraded = {
    captured: false,
    traded: false,
};

function RealOwner() {
    const classes = AppBarStyles();
    const {menu, fatherKycId, setKycId} = useMenuContext();
    const history = useHistory();
    const {
        realOwners,
        isTempRealOwnerActive,
        setEditLocation,
        setTempRealOwnerActive,
        setRealOwners,
    } = useRelatedPeopleContext();
    const {flow, moduleStep, updateModuleStep} = useFlowContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();
    const [showViews] = React.useState<{value: boolean}>({value: false});
    const views: Array<EfeViewsArray> = useViewSearch({...flow}, [...menu]);
    const [realOwnersSize, setRealOwnersSize] = React.useState<number>(1);
    const [aux, setAux] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [viewKey, setViewKey] = React.useState<string>(
        `View-${getRandomHex()}`
    );

    React.useEffect(() => {
        if (realOwners && realOwners.length === realOwnersSize && !isTempRealOwnerActive) {
            showViews.value = false;
            setAux(!aux);
            setRealOwnersSize(realOwners.length + 1);
        }
    }, [realOwners]);

    React.useEffect(() => {
        if (fatherKycId > 0 && !isTempRealOwnerActive) {
            (async function () {
                try {
                    setIsLoading(true);
                    validateTraded.captured = false;
                    validateTraded.traded = false;
                    const legalRepresentatives: EfeResponse<LegalRepresentativesResponse> =
                        await ClientService.getLegalRepresentatives(fatherKycId);
                    if (legalRepresentatives && legalRepresentatives.data) {
                        showViews.value = false;
                        await getValidateAdditionalInformation(legalRepresentatives.data.data);
                    }
                    if (!validateTraded.traded && validateTraded.captured) {
                        getRealOwners();
                    } else {
                        if (validateTraded.traded) {
                            errorDetail.data.errors = [Messages.LEGAL_REPRESENTATIVE_TRADED];
                            showAlertError(errorDetail, '');
                        } else {
                            showAlertError(errorDetail, '');
                        }
                    }
                    setIsLoading(false);
                } catch (error) {
                    showViews.value = false;
                    errorDetail.data.errors = [Messages.LEGAL_REPRESENTATIVE_NOT_CAPTURED];
                    showAlertError(errorDetail, '');
                    setIsLoading(false);
                    setRealOwners([]);
                }
            })();
        }
    }, []);

    function handleSubmit() {
        setTempRealOwnerActive(false);
        const path = nextStep(flow, menu, true);
        setKycId(-1);
        updateModuleStep(false);
        history.push(path);
    }

    async function handleNew() {
        await setKycId(-1);
        showViews.value = false;
        await setAux(!aux);
        await setEditLocation(false);
        await setTempRealOwnerActive(false);
        await setViewKey(`View-${getRandomHex()}`);
        showViews.value = true;
        history.push(HistoryRoutes.REAL_OWNER_PERSONAL_INFO);
    }

    async function handleChange(auxKycId: number) {
        showViews.value = false;
        await setEditLocation(false);
        await setKycId(-1);
        await setViewKey(`View-${getRandomHex()}`);
        await setKycId(auxKycId);
        await setTempRealOwnerActive(false);
        showViews.value = true;
        history.push(HistoryRoutes.REAL_OWNER_PERSONAL_INFO);
    }

    async function getRealOwners() {
        try {
            const response: EfeResponse<RealOwnersResponse> = await ClientService.getRealOwners(
                fatherKycId
            );
            if (response && response.data) {
                showViews.value = false;
                setKycId(0);
                const tempRepresentatives: RealOwnerInfo[] = [];
                for (const item of response.data.data) {
                    tempRepresentatives.push({
                        kycId: item.kycId || 0,
                        completeName: `${item.name} ${item.lastName} ${item.secondLastName}`,
                        foreignCitizen: item.foreignCitizen,
                    });
                }
                setRealOwners(tempRepresentatives);
                updateModuleStep(true);
            }
        } catch (error) {
            updateModuleStep(true);
            setIsLoading(false);
            setRealOwners([]);
        }
    }

    async function getValidateAdditionalInformation(
        legalRepresentatives: LegalRepresentativeServiceRequest[]
    ) {
        for (const item of legalRepresentatives) {
            try {
                const response: EfeResponse<RealOwnerAdditionalInformation> =
                    await ClientService.getRealOwnerAdditionalInformation(item.kycId);
                if (response && response.data) {
                    validateTraded.captured = true;
                    if (response.data.traded) {
                        validateTraded.traded = true;
                    }
                }
            } catch (error) {
                console.error(error);
            }
        }
    }

    return (
        <Fragment>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <MainContent>
                <Grid container direction='row' justifyContent='space-between' spacing={1}>
                    {!isLoading &&
                        realOwners &&
                        realOwners.length > 0 &&
                        realOwners.map((realOwner: RealOwnerInfo, index: number) => {
                            return (
                                <Grid item xs={12} key={`realowner-${index}`}>
                                    <EfeAccordion
                                        values={[
                                            {
                                                icon: <CheckSharp />,
                                                title: realOwner.completeName,
                                                reactElement: <div></div>,
                                                expandIcon: false,
                                                disabled: false,
                                                expanded: false,
                                            },
                                        ]}
                                        onClick={() => {
                                            handleChange(realOwner.kycId);
                                        }}
                                    />
                                </Grid>
                            );
                        })}
                </Grid>
                <Divider variant='middle' />
            </MainContent>
            <Grid container direction='row' justifyContent='space-between' key={viewKey}>
                {(showViews.value || isTempRealOwnerActive) &&
                    views &&
                    views.length > 0 &&
                    views.map((view: EfeViewsArray) => {
                        return (
                            <Grid item xs={12} key={view.index}>
                                {view.view}
                            </Grid>
                        );
                    })}
            </Grid>

            <AppBar position='fixed' color='primary' className={classes.appBar}>
                <MainContent>
                    <Grid container direction='row' justifyContent='flex-start'>
                        <Grid item xs={12}>
                            <Box display='flex' flexDirection='row' justifyContent='center' m={2}>
                                <Grid item xs={12}>
                                    <Box m={2}>
                                        <EfeButtonSecondary
                                            fullWidth
                                            variant='outlined'
                                            onClick={handleNew}
                                            disabled={!moduleStep}
                                            endIcon={<Add />}>
                                            {'Agregar propietario real'}
                                        </EfeButtonSecondary>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box m={2}>
                                        <EfeButtonPrimary
                                            fullWidth
                                            onClick={handleSubmit}
                                            disabled={
                                                !moduleStep ||
                                                !realOwners ||
                                                realOwners.length === 0
                                            }
                                            endIcon={<CallMade />}>
                                            {'Guardar y continuar'}
                                        </EfeButtonPrimary>
                                    </Box>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </MainContent>
            </AppBar>
        </Fragment>
    );
}

export default RealOwner;
